import horseIcon from '#GameFrontend/authorized/assets/ui/icon_horse.png';
import { useGameConfig } from '#GameFrontend/contexts/GameConfigContext';
import { Award, Facebook, Gift, Home, Instagram, Mail, ShoppingBag, User } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ProgressAvatar from './ProgressAvatar';
// Custom Discord Icon component
const DiscordIcon = ({ className }: { className?: string }) => (
	<svg
		viewBox="0 0 24 24"
		className={className}
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 0 0-.079.036c-.21.369-.444.85-.608 1.23a18.566 18.566 0 0 0-5.487 0 12.36 12.36 0 0 0-.617-1.23A.077.077 0 0 0 8.562 3c-1.714.29-3.354.8-4.885 1.491a.07.07 0 0 0-.032.027C.533 9.093-.32 13.555.099 17.961a.08.08 0 0 0 .031.055 20.03 20.03 0 0 0 5.993 2.98.078.078 0 0 0 .084-.026c.462-.62.874-1.275 1.226-1.963.021-.04.001-.088-.041-.104a13.201 13.201 0 0 1-1.872-.878.075.075 0 0 1-.008-.125c.126-.093.252-.19.372-.287a.075.075 0 0 1 .078-.01c3.927 1.764 8.18 1.764 12.061 0a.075.075 0 0 1 .079.009c.12.098.245.195.372.288a.075.075 0 0 1-.006.125c-.598.344-1.22.635-1.873.877a.075.075 0 0 0-.041.105c.36.687.772 1.341 1.225 1.962a.077.077 0 0 0 .084.028 19.963 19.963 0 0 0 6.002-2.981.076.076 0 0 0 .032-.054c.5-5.094-.838-9.52-3.549-13.442a.06.06 0 0 0-.031-.028zM8.02 15.278c-1.182 0-2.157-1.069-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.069-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.946 2.38-2.157 2.38z" />
	</svg>
);

// Custom VK Icon component
const VKIcon = ({ className }: { className?: string }) => (
	<svg
		viewBox="0 0 24 24"
		className={className}
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M21.579 6.855c.14-.465 0-.806-.662-.806h-2.193c-.558 0-.813.295-.953.619 0 0-1.115 2.719-2.695 4.482-.51.513-.743.675-1.021.675-.139 0-.341-.162-.341-.627V6.855c0-.558-.161-.806-.626-.806H9.642c-.348 0-.558.258-.558.504 0 .528.79.65.871 2.138v3.228c0 .707-.127.836-.407.836-.743 0-2.551-2.729-3.624-5.853-.209-.606-.42-.807-.98-.807H2.752c-.626 0-.752.295-.752.619 0 .582.743 3.462 3.461 7.271 1.812 2.601 4.363 4.011 6.687 4.011 1.393 0 1.565-.313 1.565-.853v-1.966c0-.626.133-.752.574-.752.324 0 .882.164 2.183 1.417 1.486 1.486 1.732 2.153 2.567 2.153h2.193c.626 0 .939-.313.759-.931-.197-.615-.907-1.51-1.849-2.569-.512-.604-1.277-1.254-1.51-1.579-.325-.419-.231-.604 0-.976.001 0 2.672-3.761 2.95-5.04z" />
	</svg>
);

// Calculation function for the SVG path
const calculateMaskPath = (vw: number) => {
	// Convert absolute values from the original SVG to relative values based on viewport width
	const p1 = vw / 2 - 300; // center - half of the center icon width
	const p2 = p1 - 40; // curve radius
	const p3 = p1 + 600; // enter of center and left
	const p4 = p3 + 40; // right point + radius
	const width = vw;

	return `M 0 40 L${p2} 40 C ${p1} 40, ${p2} 0, ${p1} 0 L${p3} 0 C ${p4} 0, ${p3} 40, ${p4} 40 L${width} 40 L${width} 90 L0 90 Z`;
};

export default function BottomBar() {
	const [maskPath, setMaskPath] = useState('');
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
	const { t } = useTranslation();
	const { config, isLoading } = useGameConfig();

	useEffect(() => {
		// Initial calculation
		const handleResize = () => {
			const vw = window.innerWidth;
			setMaskPath(calculateMaskPath(vw));
			setIsMobile(window.innerWidth < 768);
		};

		// Set initial path
		handleResize();

		// Add resize listener for LG breakpoint changes
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Mock data
	const footerLinks = [t('legal.header'), t('privacy.header'), t('terms.header'), t('contact.header')];
	const socialLinks = [
		{ id: 1, icon: <Facebook strokeWidth={2} className="h-5 w-5" />, url: 'https://www.facebook.com/cavalcandohq' },
		{ id: 2, icon: <Instagram strokeWidth={2} className="h-5 w-5" />, url: '#' },
		{ id: 3, icon: <VKIcon className="h-5 w-5" />, url: '#' },
		{ id: 4, icon: <DiscordIcon className="h-5 w-5" />, url: 'https://discord.gg/N4TKsg35' },
	];
	const menuItems = [
		{ id: 1, icon: <img src={horseIcon} className="h-7 w-7 lg:h-9 lg:w-9" alt="Horse" />, to: '/horse/list' },
		{ id: 2, icon: <Mail strokeWidth={2} className="h-5 w-5" />, to: '/messages' },
		{ id: 3, icon: <Mail strokeWidth={2} className="h-5 w-5" />, to: '/inbox' },
		{ id: 4, icon: <User strokeWidth={2} className="h-5 w-5" />, to: '/profile' },
		{ id: 6, icon: <ShoppingBag strokeWidth={2} className="h-5 w-5" />, to: '/shop' },
		{ id: 7, icon: <Award strokeWidth={2} className="h-5 w-5" />, to: '/achievements' },
		{ id: 8, icon: <Gift strokeWidth={2} className="h-5 w-5" />, to: '/gifts' },
		{ id: 9, icon: <Home strokeWidth={2} className="h-5 w-5" />, to: '/' },
	];

	return (
		<div className="bottom-bar-wrapper">
			{/* SVG Mask Definition */}
			<svg className="absolute w-0 h-0" viewBox={`0 0 ${window.innerWidth} 90`} preserveAspectRatio="none">
				<defs>
					<mask id="bottomBarMask">
						<path d={maskPath} fill="white" />
					</mask>
				</defs>
			</svg>

			{/* Progress Avatar Section - Hidden on mobile */}
			<div className="hidden lg:block">
				<ProgressAvatar size={150} progress={75} level={1} gradientId="bottomBarProgressGradient" />
			</div>

			{/* Bottom bar with mask */}
			<div className="bottom-bar wood-dark mask">
				{/* Left Footer Links */}
				<div className="absolute left-4 bottom-1 lg:bottom-4 flex gap-2 md:gap-4 text-white text-xs md:text-sm font-normal lg:font-thin">
					{footerLinks.map((link, index) => (
						<a key={index} href="#" className="hover:underline">
							{link}
						</a>
					))}
				</div>

				{/* Center Menu Icons */}
				<div className="menu-icons-container">
					<div className="menu-buttons">
						<div className="flex items-center gap-2 md:gap-6">
							<div className="flex gap-2 md:gap-4 mt-3">
								{menuItems.slice(0, 4).map((item) => (
									<Link
										key={item.id}
										to={item.to}
										className="rounded-lg w-8 h-9 lg:w-10 lg:h-11 border-2 border-white flex items-center justify-center cursor-pointer text-white bg-[#fb923c] hover:bg-amber-600   transition-all duration-300"
									>
										{item.id === 1
											? item.icon
											: React.cloneElement(item.icon, { className: 'h-6 w-6 text-white' })}
									</Link>
								))}
							</div>

							{/* Increased spacer for center item if progress avatar is visible (not on mobile) */}
							<div className="w-0 lg:w-32"></div>

							<div className="flex gap-2 md:gap-4 mt-3">
								{menuItems.slice(4).map((item) => (
									<Link
										key={item.id}
										to={item.to}
										className="bg-[#f39406] rounded-lg w-8 h-9 lg:w-10 lg:h-11 border-2 border-white flex items-center justify-center cursor-pointer"
									>
										{React.cloneElement(item.icon, { className: 'h-6 w-6 text-white' })}
									</Link>
								))}
							</div>
						</div>
					</div>
				</div>

				{/* Right Social Links */}
				<div className="absolute right-4 bottom-1 lg:bottom-4 flex gap-2 md:gap-4 text-white items-end">
					<span className="hidden lg:inline-block text-xs font-thin text-white/70">
						version {config?.version}
					</span>
					{socialLinks.map((link) => (
						<Link
							key={link.id}
							to={link.url}
							className="hover:text-white/80 transition-colors duration-200"
						>
							{link.icon}
						</Link>
					))}
				</div>
			</div>
		</div>
	);
}
